import { createRef } from 'react';
import {IContentBlock, IDesignBlock} from '../../types/iblock';
import { ITextBlockData } from '../../types/content-block-data';
import { escapeHTML } from '../../lib/page-builder-lib';
import pageBuilderStore from '../../stores/pageBuilderStore';
import blocksManager from '../../stores/blocks-manager';
import contentManager from '../../stores/content-manager';
import { extractCustomFieldsFromHTML } from '../../lib/page-builder-lib';

/**
 * Converts a TextBlock (IContentBlock) to an HTML string.
 * Renders as a <p> for semantic output.
 *
 * @param block - The IContentBlock (should be blockType "text").
 * @returns HTML string(e.g., <p>Text content</p>)
 * 
 * @remarks
 * - Added a new local variable `isEmail` for toggling serialization markup.
 */
export function fromTextBlockToHTML(block: IContentBlock): string {
	if (block.blockType !== "text") return "";

	const isEmail = pageBuilderStore.getIsEmail();
  const data = block.blockData as ITextBlockData;
  const htmlValue = (data.html ?? "").trim();

  if (!htmlValue) return "";

	let textStyle = `color: ${data.color || "#ffffff"}; `;
	textStyle += `font-family: ${data.fontFamily || "Inter, sans-serif"};`;
	textStyle += `font-size: ${data.fontSize?.value ? `${data.fontSize.value}${data.fontSize.unit || "rem"}` : "1rem"}; `;
	textStyle += `font-weight: ${data.fontWeight || "700"}; `;
	textStyle += `line-height: ${data.lineHeight?.value ? `${data.lineHeight.value}${data.lineHeight.unit || ""}` : "normal"};`;
	textStyle += `letter-spacing: ${data.letterSpacing?.value ? `${data.letterSpacing.value}${data.letterSpacing.unit || "px"}` : "normal"};`;
	textStyle += `text-decoration: ${data.textDecoration || "none"};`;
	textStyle += `text-transform: ${data.textTransform || "none"};`;
	textStyle += `text-align: ${data.alignment || "left"};`;
	textStyle += `transition: all 0.15s;`;

	const htmlForExport = pageBuilderStore.getIsEmail() 
		? data.html.replace(
  		/<span[^>]*data-custom-field="true"[^>]*data-field-value="({.*?})"[^>]*contenteditable="false"[^>]*>.*?<\/span>/g,
  		'$1'
		) : data.html;

  return isEmail 
		? `<tr><td class="tb-content-area ${block.selector}" style="${textStyle}">${htmlForExport}</td></tr>` 
		: `<div class="tb-content-area ${block.selector}" style="${textStyle}">${htmlForExport}</div>`;
}

/**
 * Serializes a TextBlock's data for storage or export.
 * Omits UI-only fields.
 *
 * @param block - The IContentBlock (should be blockType "text").
 * @returns A JSON-serializable object
 */
export function fromTextBlockToJSON(block: IContentBlock): object {
	if (block.blockType !== "text") return {};
	const data = block.blockData as ITextBlockData;

	return {
		contentBlockId: block.contentBlockId,
		blockType: block.blockType,
		selector: block.selector,
		blockData: {
			html: data.html ?? "",
		},
		// Add any additional props you store for TextBlocks
	};
}

/**
 * Reconstructs a TextBlock from JSON data.
 *
 * @param json - The JSON object to parse.
 * @returns An IContentBlock instance (for use in our store/UI).
 */
export function fromJSONToTextBlock(json: any, context: "canvas" | "columns" = "canvas"): IDesignBlock {
	const cssContent = contentManager.getStylesForSelector(json.selector);
    
  const newDesignBlockRef = createRef<HTMLDivElement>();
  const newContentBlockRef = createRef<HTMLElement>(); // generic for now

	const block: IDesignBlock = {
		designBlockId: crypto.randomUUID(), // always generate fresh
		designBlockRef: newDesignBlockRef,
		contentBlock: {
			contentBlockId: json.contentBlockId,
			blockType: json.blockType,
			contentBlockRef: newContentBlockRef, // You will want to assign/create a React ref in the UI code!
			selector: json.selector ?? undefined,
			blockData: {
				html: json.blockData?.html ?? "",
				fontFamily: cssContent?.["font-family"] ?? "Inter, sans-serif",
				fontSize: blocksManager.parseDimensions(cssContent?.["font-size"], "single"),
				fontWeight: cssContent?.["font-weight"] ?? "400",
				color: cssContent!["color"] ?? "#222222",
				lineHeight: blocksManager.parseDimensions(cssContent?.["line-height"], "single"),
				letterSpacing: blocksManager.parseDimensions(cssContent?.["letter-spacing"], "single"),
				textDecoration: cssContent?.["text-decoration"] ?? "none",
				textTransform: cssContent?.["text-transform"] ?? "none",
				alignment: cssContent?.["text-align"] ?? "left",
			} as ITextBlockData,
		} as IContentBlock
	};

	blocksManager.addDesignTreeEntry({
		contentBlockId: block.contentBlock.contentBlockId,
		designBlockId: block.designBlockId,
		designBlockRef: newDesignBlockRef,
		contentBlockRef: newContentBlockRef,
		contentBlockType: block.contentBlock.blockType.toUpperCase(),
		location: context,
	});

	// ✅ Hydrate addedFields for this block on load (email + edit mode only)
  if (pageBuilderStore.getIsEmail() && pageBuilderStore.getIsEditingContent()) {
    const html = (block.contentBlock.blockData as ITextBlockData).html || "";
    const tokens = pageBuilderStore.getCustomFields();
    const fields = extractCustomFieldsFromHTML(html, tokens);
    if (fields.length) {
      // merge with any existing (defensive), then dedupe
      const existing = pageBuilderStore.getAddedFields(json.contentBlockId) || [];
      const merged = Array.from(new Set([...existing, ...fields]));
      pageBuilderStore.updateAddedFields(json.contentBlockId, merged);
    }
  }

	return block;
}
